export const CREDITS_PER_DOLLAR = 1000;
export const CENTS_PER_DOLLAR = 100;
export const CREDITS_PER_CENT = 10;

export function centsToWalletCredits(balanceCents) {
  return Math.trunc(Number(balanceCents) || 0) * CREDITS_PER_CENT;
}

export function walletCreditsToCents(walletCredits) {
  const credits = Math.trunc(Number(walletCredits) || 0);
  if (credits % CREDITS_PER_CENT !== 0) {
    return null;
  }
  return credits / CREDITS_PER_CENT;
}

export function formatDollarsFromCents(cents) {
  const value = (Math.trunc(Number(cents) || 0) / CENTS_PER_DOLLAR).toFixed(2);
  return `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function formatCredits(credits) {
  return `${Math.trunc(Number(credits) || 0).toLocaleString('en-US')} credits`;
}

export function formatDollarsFromCredits(credits) {
  const cents = Math.trunc(Number(credits) || 0) / CREDITS_PER_CENT;
  return formatDollarsFromCents(cents);
}

export function parseDollarsToCents(input) {
  const raw = String(input || '').trim().replace(/[$,]/g, '');
  if (!raw) return null;
  if (!/^\d+(\.\d{0,2})?$/.test(raw)) return null;
  const [whole, fraction = ''] = raw.split('.');
  const cents = Number(whole) * CENTS_PER_DOLLAR + Number((fraction + '00').slice(0, 2));
  return cents > 0 ? cents : null;
}

export function parseCreditsInput(input) {
  const raw = String(input || '').trim().replace(/,/g, '');
  if (!raw || !/^\d+$/.test(raw)) return null;
  const credits = Number(raw);
  return credits > 0 ? credits : null;
}

export function deriveCreditsFromDollarsInput(dollarsInput) {
  const cents = parseDollarsToCents(dollarsInput);
  if (cents == null) return null;
  return centsToWalletCredits(cents);
}

export function deriveDollarsDisplayFromCreditsInput(creditsInput) {
  const credits = parseCreditsInput(creditsInput);
  if (credits == null) return null;
  return formatDollarsFromCredits(credits);
}

export function canActorManageWallet(actorRole, actorAgencyId, drawerAgencyId) {
  if (['platform_owner', 'platform_admin', 'super_admin'].includes(actorRole)) {
    return true;
  }
  if (['owner', 'specialist'].includes(actorRole)) {
    return Boolean(actorAgencyId && drawerAgencyId && actorAgencyId === drawerAgencyId);
  }
  return false;
}

export function canActorManageTargetWallet(targetRole, actorRole) {
  if (['platform_owner', 'super_admin'].includes(targetRole)) {
    return ['platform_owner', 'super_admin'].includes(actorRole);
  }
  return true;
}

export const ADJUSTMENT_TYPES = {
  ADD_BALANCE: 'add_balance',
  DEBIT_BALANCE: 'debit_balance',
  ADD_COMPLIMENTARY: 'add_complimentary_credits',
  DEBIT_COMPLIMENTARY: 'debit_complimentary_credits',
};

export const ADJUSTMENT_OPTIONS = [
  { value: ADJUSTMENT_TYPES.ADD_BALANCE, label: 'Add Balance' },
  { value: ADJUSTMENT_TYPES.DEBIT_BALANCE, label: 'Debit Balance' },
  { value: ADJUSTMENT_TYPES.ADD_COMPLIMENTARY, label: 'Add Complimentary Credits' },
  { value: ADJUSTMENT_TYPES.DEBIT_COMPLIMENTARY, label: 'Debit Complimentary Credits' },
];

export function isDebitAdjustment(type) {
  return type === ADJUSTMENT_TYPES.DEBIT_BALANCE || type === ADJUSTMENT_TYPES.DEBIT_COMPLIMENTARY;
}

export function isBalanceAdjustment(type) {
  return type === ADJUSTMENT_TYPES.ADD_BALANCE || type === ADJUSTMENT_TYPES.DEBIT_BALANCE;
}

export function validateAdjustmentForm({
  adjustmentType,
  amountType,
  amountInput,
  reason,
  debitConfirmed,
  wallet,
}) {
  const errors = [];
  if (!adjustmentType) errors.push('Select an adjustment type.');
  if (!amountInput?.trim()) errors.push('Enter an amount greater than zero.');

  let amountCents = null;
  let credits = null;

  if (amountType === 'dollars') {
    amountCents = parseDollarsToCents(amountInput);
    if (amountInput?.trim() && amountCents == null) {
      errors.push('Enter a valid dollar amount (up to 2 decimals).');
    }
  } else {
    credits = parseCreditsInput(amountInput);
    if (amountInput?.trim() && credits == null) {
      errors.push('Credits must be a positive whole number.');
    }
    if (credits != null && isBalanceAdjustment(adjustmentType) && credits % CREDITS_PER_CENT !== 0) {
      errors.push(`Paid wallet credits must be in multiples of ${CREDITS_PER_CENT}.`);
    }
  }

  if (!reason?.trim() || reason.trim().length < 3) {
    errors.push('Reason must be at least 3 characters.');
  }

  if (isDebitAdjustment(adjustmentType) && !debitConfirmed) {
    errors.push('Confirm that you understand this adjustment will debit the user account.');
  }

  if (wallet && adjustmentType === ADJUSTMENT_TYPES.DEBIT_BALANCE) {
    const debitCents =
      amountType === 'dollars' ? amountCents : walletCreditsToCents(credits);
    if (debitCents != null && debitCents > (wallet.balance_cents || 0)) {
      errors.push('Debit exceeds available wallet balance.');
    }
  }

  if (wallet && adjustmentType === ADJUSTMENT_TYPES.DEBIT_COMPLIMENTARY) {
    const debitCredits =
      amountType === 'dollars' ? deriveCreditsFromDollarsInput(amountInput) : credits;
    if (debitCredits != null && debitCredits > (wallet.complimentary_credits || 0)) {
      errors.push('Debit exceeds available complimentary credits.');
    }
  }

  return {
    valid: errors.length === 0 && (amountCents != null || credits != null),
    errors,
    amountCents,
    credits,
  };
}
